/**
 * "What's new" announcements (C-16 S0, GLE-8407). Every release that changes
 * what a role sees ships an entry; the widget shows the live ones at the
 * start of the day's first visit. Everything here is plain JSON (icons are
 * keys, links are strings), so a backend feed can send the same shapes.
 */

import type { ManageFeaturesI } from "@/api/routes/manage";

/** Who an announcement is for: the viewer's strongest role (C16-D313). */
export const WHATS_NEW_AUDIENCES = {
  CALLER: "caller",
  POD_LEAD: "pod_lead",
  LGA: "lga",
  ADMIN: "admin",
} as const;

export type WhatsNewAudienceT =
  (typeof WHATS_NEW_AUDIENCES)[keyof typeof WHATS_NEW_AUDIENCES];

/** Section icons, by key; the dialog maps each to an icon component. */
export const WHATS_NEW_ICONS = {
  CALENDAR: "calendar",
  HEALTH: "health",
  STRIKE: "strike",
  SCORE: "score",
  TEAM: "team",
  BELL: "bell",
  TROPHY: "trophy",
  SHIELD: "shield",
  GIFT: "gift",
  SPARKLES: "sparkles",
} as const;

export type WhatsNewIconT =
  (typeof WHATS_NEW_ICONS)[keyof typeof WHATS_NEW_ICONS];

export interface WhatsNewLinkI {
  label: string;
  /** An in-app path, e.g. "/my-week". */
  href: string;
}

export interface WhatsNewSectionI {
  id: string;
  icon: WhatsNewIconT;
  title: string;
  /** Short lines, one idea each. */
  items: Array<string>;
  link?: WhatsNewLinkI;
}

/** One announcement, resolved for one viewer: what the dialog renders. */
export interface WhatsNewCardI {
  /** Stable: a new id announces again, an unchanged one stays dismissed. */
  id: string;
  /** ISO instant the card goes live. */
  startsAt: string;
  /** ISO instant it stops showing (exclusive). */
  endsAt: string;
  title: string;
  intro?: string;
  sections: Array<WhatsNewSectionI>;
  /** The ET days a user sees it on, at most (C16-D425). */
  maxDaysPerUser?: number;
}

/**
 * A user sees an announcement on at most this many ET days (C16-D425), so a
 * wide window (a dark page's release → +14 days) still shows it for two days
 * once the page is on.
 */
export const DEFAULT_WHATS_NEW_MAX_DAYS = 2;

/** A section of a static entry, shown to the audiences it lists. */
export interface WhatsNewEntrySectionI extends WhatsNewSectionI {
  audiences: Array<WhatsNewAudienceT>;
}

/**
 * A release's announcement, written once for every role (C16-D314): each
 * section says who sees it, and the title and intro are per audience. An
 * audience with no title or no sections gets no card.
 */
export interface WhatsNewEntryI {
  id: string;
  startsAt: string;
  endsAt: string;
  /**
   * A `manage/me` features key (e.g. "pipeline"): the entry shows only while
   * that page is switched on, so a page that ships dark is announced from
   * its switch-on, inside the entry's window (C16-D425).
   */
  feature?: keyof ManageFeaturesI;
  /** The ET days a user sees it on, at most; DEFAULT_WHATS_NEW_MAX_DAYS. */
  maxDaysPerUser?: number;
  title: Partial<Record<WhatsNewAudienceT, string>>;
  intro?: Partial<Record<WhatsNewAudienceT, string>>;
  sections: Array<WhatsNewEntrySectionI>;
}
